import { Immutable } from "immer";
import React, { useRef } from "react";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { HeroImageBlock } from "../../blocks/HeroImageBlock";

/**
 * toppers HomePage ``hero_image_list`` carousel assembly:
 * dispatches every block type in the ``HeroImageListBlock`` StreamField to its
 * React component, collapsing the per-block
 * ``{% for block in self.hero_image_list %}{% include_block block %}{% endfor %}``
 * loop into a SINGLE ``{% include_react %}`` island.
 *
 * LOAD-BEARING container ownership: this component renders the
 * ``.home-page-hero-carousel__container`` div itself, with the bare slides as its
 * DIRECT children (the page template keeps the outer ``.home-page-hero-carousel``
 * ``role=region`` wrapper). Flickity treats those direct children as carousel
 * cells, so introducing an intermediate wrapper -- per slide or around the whole
 * list -- would break it.
 *
 * CAROUSEL INIT LIVES HERE (not in ``client/src/ui/homepage.ts``): this component
 * owns the container DOM, so it owns its own Flickity lifecycle via the shared
 * ``useFlickity`` hook. The hook is SSR-safe -- it dynamic-imports flickity inside
 * a client-only effect (a TOP-LEVEL ``import`` would crash the SSR renderer,
 * because flickity's UMD factory references ``window`` at evaluation time) and
 * tears down with ``safeDestroy``. The ``enabled`` gate reproduces the
 * homepage.ts behavior: only a real multi-slide LARGE-SCREEN carousel (>1 rendered
 * ``section.responsive-visibility--large-only`` cell) initializes; a single
 * desktop slide (e.g. one large-only + one small-only mobile slide) renders
 * statically. Only ``product_callout`` renders that ``<section>`` (HeroImageBlock
 * renders a bare div), so we count product_callout slides whose
 * ``responsive_visibility`` is large-only
 * (``ProductCalloutBlock.LARGE_ONLY === "responsive-visibility--large-only"``).
 *
 * NO PER-BLOCK WRAPPER: the loop emitted each block via a bare
 * ``{% include_block block %}`` (each block's ``Meta.template`` was a 2-line
 * ``{% include_react %}`` of its per-block ``@template``), so Wagtail's
 * ``StreamBlock.render_basic`` ``<div class="w-block-...">`` wrapper was never
 * applied. ``StreamField`` with ``tagName={React.Fragment}`` renders each slide
 * bare: ``HeroImageBlock`` -> ``<div class="home-page-hero-carousel__image
 * home-page-hero-height">``, ``product_callout`` -> ``<section
 * class="product-callout-block ...">`` -- matching the per-block output.
 *
 * The promo ribbon + ``home-page-hero-content`` pricing (``purchase_info_for_product``,
 * ``{% slugurl %}``) and the mobile background-image div STAY server-rendered in
 * ``home_page.html`` -- intentionally NOT part of this assembly.
 */
export const heroCarouselComponentsMap = {
    hero_image_list: HeroImageBlock,
    product_callout: ProductCalloutBlock,
} as const;

const FLICKITY_OPTIONS = {
    watchCSS: true,
    autoPlay: true,
    pageDots: false,
};

export const HomePageHeroCarousel = (
    props: Immutable<{
        page: Pick<templates.HomePageTemplate["page"], "hero_image_list">;
    }>,
): React.JSX.Element | null => {
    const heroImageList = props.page.hero_image_list;
    const containerRef = useRef<HTMLDivElement>(null);

    const largeOnlyCellCount = (heroImageList?.value ?? []).filter(
        (block) =>
            block.type === "product_callout" &&
            block.value.responsive_visibility?.visibility ===
                "responsive-visibility--large-only",
    ).length;

    useFlickity(containerRef, {
        options: FLICKITY_OPTIONS,
        enabled: largeOnlyCellCount > 1,
    });

    if (!heroImageList) {
        return null;
    }
    return (
        <div className="home-page-hero-carousel__container" ref={containerRef}>
            <StreamField
                tagName={React.Fragment}
                value={heroImageList.value}
                components={heroCarouselComponentsMap}
            />
        </div>
    );
};
